import React from "react";
import { formatToDateFull } from "../master/FormatToDateTime";
import {
  buildMarksheet,
  getRemark,
  nextClass,
  getSummaryItems,
  FILL_IN_ITEMS,
} from "./marksheetUtils";
import {
  useSetting,
  getLogo,
  schoolAddress,
  schoolContact,
  getStudentFields,
  MarksheetStyle,
  MarksTable,
  CoScholastic,
  GradeLegend,
} from "./marksheetShared";

// Marksheet 5: Result dashboard. Full-width green header band, percentage
// as the hero figure, and a score bar for every subject.

const CSS = `
& { padding: 0; }
& .ms5-band { display: flex; align-items: center; gap: 14px; padding: 9mm 12mm 7mm; background: rgba(22,163,74,.15); }
& .ms5-logo { width: 62px; height: 62px; object-fit: contain; }
& .ms5-school { flex: 1; }
& .ms5-name { font-size: 20px; font-weight: 800; line-height: 1.15; }
& .ms5-meta { font-size: 10px; opacity: .75; }
& .ms5-doc { text-align: right; }
& .ms5-doc-title { font-size: 17px; font-weight: 800; }

& .ms5-body { padding: 8mm 12mm; }
& .ms5-hero { display: grid; grid-template-columns: 1.2fr 1.4fr .8fr 1fr .8fr; gap: 8px; }
& .ms5-stat { padding: 8px 10px; border-radius: 10px; background: rgba(22,163,74,.09); }
& .ms5-stat span { display: block; font-size: 9px; opacity: .7; }
& .ms5-stat b { font-size: 16px; font-weight: 800; }
& .ms5-stat--main { background: rgba(22,163,74,.22); }
& .ms5-stat--main b { font-size: 24px; }

& .ms5-info { display: grid; grid-template-columns: 1fr 1fr; column-gap: 26px; margin: 12px 0; }
& .ms5-info div { display: flex; justify-content: space-between; gap: 8px; padding: 4px 0; border-bottom: .5pt solid rgba(0,0,0,.25) !important; }
& .ms5-info span { opacity: .7; }
& .ms5-info b { font-weight: 700; text-align: right; }

& .ms-marks th, & .ms-cos th { padding: 6px; text-align: center; font-weight: 800; background: rgba(22,163,74,.2); }
& .ms-marks td, & .ms-cos td { padding: 5px 6px; text-align: center; border-bottom: .5pt solid rgba(0,0,0,.2) !important; }
& .ms-marks tfoot td { font-weight: 800; background: rgba(22,163,74,.1); border-bottom: 0 !important; }
& .ms-cos th:first-child, & .ms-cos td:first-child { text-align: left; }
& .ms-bar { width: 24mm; }
& .ms-bar span { display: block; height: 6px; border-radius: 3px; background: rgba(0,0,0,.09); }
& .ms-bar i { display: block; height: 100%; border-radius: 3px; background: rgba(22,163,74,.85); }

& .ms5-lower { display: grid; grid-template-columns: 1.1fr 1fr; gap: 16px; margin-top: 12px; align-items: start; }
& .ms5-fill { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-bottom: 10px; }
& .ms5-fill div { min-height: 38px; padding: 6px 8px; border-radius: 8px; background: rgba(22,163,74,.09); }
& .ms5-fill span, & .ms5-note span { display: block; font-size: 9px; opacity: .65; }
& .ms5-note { margin-bottom: 7px; }
& .ms5-note p { min-height: 20px; font-weight: 600; border-bottom: .5pt solid rgba(0,0,0,.25) !important; }

& .ms5-sign { display: flex; gap: 24px; margin-top: 26px; }
& .ms5-sign div { flex: 1; text-align: center; padding-top: 4px; border-top: 1px solid #000 !important; }
& .ms-legend { margin-top: 12px; }
`;

export default function StudentMarksheet5({ data, batch, examDate }) {
  const setting = useSetting();
  const sheet = buildMarksheet(data, batch);
  const logo = getLogo(setting);
  const fields = getStudentFields(data, batch);
  const summary = getSummaryItems(sheet.overall);
  const remark = getRemark(sheet.overall.percentage, data?.student_id);

  return (
    <>
      <MarksheetStyle setting={setting} scope="ms5" css={CSS} />

      <div className="a4-page-marksheet ms5">
        <div className="ms5-band">
          {logo && <img className="ms5-logo" src={logo} alt="" />}
          <div className="ms5-school">
            <div className="ms5-name">{setting?.name}</div>
            <div className="ms5-meta">{schoolAddress(setting)}</div>
            <div className="ms5-meta">{schoolContact(setting)}</div>
          </div>
          <div className="ms5-doc">
            <div className="ms5-doc-title">Report Card</div>
            <div className="ms5-meta">Session {batch?.sessions?.session}</div>
          </div>
        </div>

        <div className="ms5-body">
          <div className="ms5-hero">
            {summary.map((t) => (
              <div
                className={`ms5-stat ${
                  t.label === "Percentage" ? "ms5-stat--main" : ""
                }`}
                key={t.label}
              >
                <span>{t.label}</span>
                <b>{t.value}</b>
              </div>
            ))}
          </div>

          <div className="ms5-info">
            {fields.map((f) => (
              <div key={f.label}>
                <span>{f.label}</span>
                <b>{f.value || "-"}</b>
              </div>
            ))}
          </div>

          <MarksTable {...sheet} bars />

          <div className="ms5-lower">
            <CoScholastic />
            <div>
              <div className="ms5-fill">
                {FILL_IN_ITEMS.map((label) => (
                  <div key={label}>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="ms5-note">
                <span>Class teacher's remark</span>
                <p>{remark}</p>
              </div>
              <div className="ms5-note">
                <span>Promoted to class</span>
                <p>{nextClass(batch?.course?.title)}</p>
              </div>
              <div className="ms5-note">
                <span>Date</span>
                <p>{formatToDateFull(examDate)}</p>
              </div>
            </div>
          </div>

          <div className="ms5-sign">
            <div>Class Teacher</div>
            <div>Parent</div>
            <div>Principal</div>
          </div>

          <GradeLegend />
        </div>
      </div>
    </>
  );
}
